import React, {useState, useEffect, useCallback, useRef} from 'react';
import {t} from 'i18next';
import {Box, Grid, Stack, Typography} from '@mui/material';
import {v4 as uuidv4} from 'uuid';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../redux/store';
import {
  PersistSearches,
  AddUnsavedSearch,
  ClearShoppingCart,
} from '../../redux/slice/sessionSearchSlice';
import './ViewSessionSearch.scss';
import {ResultType, SessionSearch} from './types/SessionSearch';
import LeftHeader from './page-header/left-header/LeftHeader';
import CenterHeader from './page-header/center-header/CenterHeader';
import SearchList from './search-list/SearchList';
import SearchModifiers from './search-modifiers/SearchModifiers';
import SearchQuery from './search-query/SearchQuery';
import SaveSearchDrawer from './save-search-drawer/SaveSearchDrawer';
import {Query, QueryType} from './types/Query';
import {ReactComponent as InfoIcon} from '../../assets/images/info-icon.svg';
import {
  buildCriteriaString,
  createSearchRequest,
  createSearchResultRequest,
  getCriteriaValues,
  hasAnyCriteriaAdded,
  toSearchResult,
  isExecuted,
  toSearchResultRequest,
  createEmptySearchResult,
} from './utilities/utilities';
import {
  useAddNotification,
  useReloadSearchResult,
  useRunSearch,
  useSearchStatus,
  useValidateAnalyticCriteria,
  useValidateContentmetaData,
  useValidateSearchCriteria,
  useValidateWorkProductCriteria,
  useGetSearchUniqueDocIds,
} from '../../hooks/session-search/useSessionSearch';
import ArrModal from '../common/arr-modal/ArrModal';
import {
  SearchResultRequest,
  SessionSearchItem,
} from '../../api/types/sessionSearch.type';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../shared/constants';
import ArrLoadingSpinner from '../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  SEARCHFAILED_SIGNALR_TYPE,
  SEARCHSUCCESS_SIGNALR_TYPE,
  SessionSearchColors,
} from './utilities/constants';
import {Criteria} from './types/Criteria';
import ResultShoppingCart from './result-shopping-cart/ResultShoppingCart';
import {useLocation} from 'react-router-dom';
import {ArrSearchProgressDialog} from '../common/arr-search-progress-dialog/ArrSearchProgressDialog';
import {SignalREvent} from '../../redux/slice/signalrSlice';
import ArrDataGrid from '../common/arr-data-grid/ArrDataGrid';
import ArrButton from '../common/arr-button/ArrButton';
import ArrBanner from '../common/arr-banner/ArrBanner';
import {DEFAULT_ROW_HEIGHT} from '../common/lazy-load-data-grid/constant';
import {ReactComponent as CompleteIcon} from '../../assets/images/complete-icon.svg';
import {ReactComponent as ExportIcon} from '../../assets/images/export.svg';
import {nestedColumns} from '../../api/types/nestedsearch.types';
import {NestedSearchResponse} from '../../api/types/savedSearch.type';

class DefaultSessionSearch implements SessionSearch {
  id = uuidv4();
  name = '';
  familyMemberDocuments = false;
  threadedEmailDocuments = false;
  nearDuplicateDocuments = false;
  regexOnWorkProductAttributes = false;
  isSelected = false;
  isPinned = false;
  isSaved = false;
  lastRuntime?: Date = undefined;
  advancedSearchId?: number = undefined;
  query: Query[] = [
    {type: QueryType.ContentAndMetadata, value: [], operator: 'AND'},
  ];
}

class SearchValidationAbortError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SearchValidationAbortError';
  }
}

const ViewSessionSearch: React.FC = () => {
  const [sessionSearches, setSessionSearches] = useState<SessionSearch[]>([
    new DefaultSessionSearch(),
  ]);
  const [displaySearches, setDisplaySearches] = useState<SessionSearch[]>([
    new DefaultSessionSearch(),
  ]);
  const [currentSessionSearch, setCurrentSessionSearch] =
    useState<SessionSearch>(new DefaultSessionSearch());
  const hasLoadedFromNavigation = useRef(false);
  const hasInitializedFromRedux = useRef(false);
  const hasHandledBackNavigation = useRef(false);
  const selectionApplied = useRef(false);
  const [uniqueDocIdCount, setUniqueDocIdCount] = useState<number>(0);
  const userRoleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );
  const projectId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.projectId
  );
  const userSecurityGroup = useSelector(
    (state: RootState) => state.authenticatedUser.user?.securityGroupId
  );

  const [
    showAnalyticWorProductValidation,
    setShowAnalyticWorProductValidation,
  ] = useState<boolean>(false);
  const [showSearchToast, setShowSearchToast] = useState<boolean>(false);
  const [showContentMDValidation, setShowContentMDValidation] =
    useState<boolean>(false);
  const [readyForSearch, setReadyForSearch] = useState<boolean>(false);
  const [canContinueWithSearch, setcanContinueWithSearch] =
    useState<boolean>(false);
  const [criticalValidationError, setCriticalValidationError] =
    useState<string>('');
  const [errorStringContainsHtml, setErrorStringContainsHtml] =
    useState<boolean>(false);
  const [contentMDValidationError, setContentMDValidationError] =
    useState<string>('');
  const [toastType, setToastType] = useState(ToastType.Warning);
  const [searchToastMessage, setSearchToastMessage] = useState<string>('');
  const [currentSearchResultRequest, setCurrentSearchResultRequest] =
    useState<SearchResultRequest | null>(null);
  const [searchIsInProgress, setSearchIsInProgress] = useState<boolean>(false);
  const validateAnalytic = useValidateAnalyticCriteria();
  const validateWorkProduct = useValidateWorkProductCriteria();
  const validateContentMeta = useValidateContentmetaData();
  const validateSearchCriteria = useValidateSearchCriteria();
  const [canAddItem, setCanAddItem] = useState<boolean>(false);
  const [canDiscardItem, setCanDiscardItem] = useState<boolean>(false);
  const [canPinItem, setCanPinItem] = useState<boolean>(true);
  const [currentSessionSearchBackup, setCurrentSessionSearchBackup] =
    useState<SessionSearch | null>(null);
  const [isSaveDrawerOpen, setIsSaveDrawerOpen] = useState<boolean>(false);
  const [showSaveSuccessToast, setShowSaveSuccessToast] =
    useState<boolean>(false);
  const [showPinToast, setShowPinToast] = useState<boolean>(false);
  const [shouldPollStatus, setShouldPollStatus] = useState(false);
  const [shouldPollResults, setShouldPollResults] = useState(false);
  const [showSearchWaitModal, setShowSearchWaitModal] = useState(false);
  const [nestedChildModal, setNestedChildModal] = useState(false);
  const [sessionSearchItem, setSessionSearchItem] = useState<SessionSearch>();
  const [openNestedSearchModal, setOpenNestedSearchModal] = useState(false);
  const [rows] = useState<NestedSearchResponse[]>([]);
  const location = useLocation();
  const dispatch = useDispatch();
  const persistentSearches = useSelector(
    (state: RootState) => state.sessionSearch.persistentSearches
  );
  const signalREvents = useSelector((state: RootState) => state.signalr.events);
  const projectContextKey = `${projectId ?? ''}|${userRoleId ?? ''}|${userSecurityGroup ?? ''}`;
  const lastProjectContextKey = useRef<string | null>(null);
  type PollPhase = 'initial' | 'extended' | 'final' | null;
  const [pollPhase, setPollPhase] = useState<PollPhase>(null);
  const [pollCount, setPollCount] = useState(0);
  const searchId = currentSessionSearch.advancedSearchId;
  const {data: searchStatusResponse, refetch: refetchSearchStatus} =
    useSearchStatus(currentSessionSearch.advancedSearchId ?? 0);

  const {mutateAsync: sendNotification} = useAddNotification();

  const {mutateAsync: runsearch} = useRunSearch(
    (data) => {
      if (data.sessionSearchList.length > 0) {
        const searchResultRequest = createSearchResultRequest(data);
        currentSessionSearch.advancedSearchId = searchResultRequest.searchId;
        setCurrentSessionSearch({...currentSessionSearch});
        setCurrentSearchResultRequest(searchResultRequest);
      }
    },
    (err) => {
      handleSearchFailure();
      console.error('Search failed:', err.message);
    }
  );

  const {mutateAsync: loadsearchResult} = useReloadSearchResult();
  const isNestedSearchLoading = false;

  const {mutate: getSearchUniqueDocIds} = useGetSearchUniqueDocIds(
    (data) => {
      setUniqueDocIdCount(data.indexDocCount ?? 0);
    },
    (err) => {
      console.error('API FAILED:', err);
      setUniqueDocIdCount(0);
    }
  );

  useEffect(() => {
    if (
      !hasInitializedFromRedux.current ||
      !sessionSearches.length ||
      currentSessionSearch.searchIsInProgress
    ) {
      return;
    }

    if (lastProjectContextKey.current === null) {
      lastProjectContextKey.current = projectContextKey;
    }

    if (lastProjectContextKey.current !== projectContextKey) {
      lastProjectContextKey.current = projectContextKey;

      const newSearch = new DefaultSessionSearch();
      newSearch.isSelected = true;

      setCanDiscardItem(false);
      setCurrentSessionSearchBackup(null);
      setCurrentSearchResultRequest(null);
      setShouldPollStatus(false);
      setShouldPollResults(false);
      setSearchIsInProgress(false);
      setUniqueDocIdCount(0);

      dispatch(ClearShoppingCart());
      dispatch(PersistSearches([]));
      sessionStorage.removeItem('presistence-search');

      setSessionSearches([newSearch]);
      setDisplaySearches([newSearch]);
      setCurrentSessionSearch(newSearch);

      return;
    }

    sessionStorage.setItem(
      'presistence-search',
      JSON.stringify({
        searches: sessionSearches,
        currentId: currentSessionSearch.id,
        projectId,
        userRoleId,
        userSecurityGroupId: userSecurityGroup,
      })
    );
  }, [
    projectContextKey,
    currentSessionSearch,
    sessionSearches,
    projectId,
    userRoleId,
    userSecurityGroup,
    dispatch,
  ]);

  useEffect(() => {
    return () => {
      hasHandledBackNavigation.current = false;
    };
  }, [location.pathname]);

  // STEP 1: Handle navigation state ONCE on mount
  useEffect(() => {
    const loadedSearch = location.state?.loadedSearch as
      | SessionSearch
      | undefined;
    const loadedSearches = location.state?.loadedSearches as
      | SessionSearch[]
      | undefined;
    if ((loadedSearch || loadedSearches) && !hasLoadedFromNavigation.current) {
      hasLoadedFromNavigation.current = true;
      hasInitializedFromRedux.current = true;
      let searchesToLoad: SessionSearch[] = [];
      if (loadedSearches && loadedSearches.length > 0) {
        searchesToLoad = loadedSearches;
      } else if (loadedSearch) {
        searchesToLoad = [loadedSearch];
      }
      const selectedSearchId =
        (searchesToLoad.find((s) => s.isSelected) || searchesToLoad[0])?.id ??
        '';
      searchesToLoad = searchesToLoad.map((search) => ({
        ...search,
        isSelected: search.id === selectedSearchId,
      }));
      let persistedSearches: SessionSearch[] = [];

      try {
        persistedSearches =
          JSON.parse(sessionStorage.getItem('presistence-search') || '{}')
            ?.searches ?? [];
      } catch {
        persistedSearches = [];
      }

      setSessionSearches((prevSearches) => {
        const mergedSearches = [
          ...(persistedSearches.length
            ? persistedSearches
            : prevSearches?.length
              ? prevSearches
              : (persistentSearches ?? [])),
        ];

        searchesToLoad.forEach((loadedSearch) => {
          const index = mergedSearches.findIndex(
            (search) =>
              search.id === loadedSearch.id ||
              (!!search.advancedSearchId &&
                !!loadedSearch.advancedSearchId &&
                search.advancedSearchId === loadedSearch.advancedSearchId)
          );

          index >= 0
            ? // eslint-disable-next-line security/detect-object-injection
              (mergedSearches[index] = {
                // eslint-disable-next-line security/detect-object-injection
                ...mergedSearches[index],
                ...loadedSearch,
              })
            : mergedSearches.unshift(loadedSearch);
        });

        return mergedSearches.map((search) => ({
          ...search,
          isSelected: search.id === selectedSearchId,
        }));
      });
      const selectedSearch =
        searchesToLoad.find((s) => s.isSelected) || searchesToLoad[0];
      if (selectedSearch) {
        selectionApplied.current = true;
        setCurrentSessionSearch(selectedSearch);
      }
      searchesToLoad.forEach((search) => {
        dispatch(AddUnsavedSearch(search));
      });
    }
  }, [dispatch]);

  // STEP 2: Initialize from Redux when it updates
  useEffect(() => {
    if (hasInitializedFromRedux.current) {
      return;
    }
    const savedPresistance = sessionStorage.getItem('presistence-search');
    hasInitializedFromRedux.current = true;

    let persistedSearches: SessionSearch[] = [];
    let persistedProjectId: string | number | null = null;
    let persistedUserRoleId: string | number | null = null;
    let persistedSecurityGroupId: string | number | null = null;
    let persistedCurrentId: string | null = null;

    if (savedPresistance) {
      try {
        const parsed = JSON.parse(savedPresistance);
        persistedSearches = (parsed.searches as SessionSearch[]) ?? [];
        persistedProjectId = parsed.projectId ?? null;
        persistedUserRoleId = parsed.userRoleId ?? null;
        persistedSecurityGroupId = parsed.userSecurityGroupId ?? null;
        persistedCurrentId = parsed.currentId ?? null;
      } catch {
        persistedSearches = [];
      }
    }

    // Start from Redux persisted searches
    const localSearches: SessionSearch[] = JSON.parse(
      JSON.stringify(persistentSearches ?? [])
    );
    const hasContextChanged =
      (persistedProjectId !== null && persistedProjectId !== projectId) ||
      (persistedUserRoleId !== null && persistedUserRoleId !== userRoleId) ||
      (persistedSecurityGroupId !== null &&
        persistedSecurityGroupId !== userSecurityGroup);

    if (hasContextChanged) {
      persistedSearches = [];
      dispatch(ClearShoppingCart());
      dispatch(PersistSearches([]));
      sessionStorage.removeItem('presistence-search');
      persistedCurrentId = null;
      localSearches.length = 0;
    }
    //merge persistedSearches from sessionStorage into localSearches
    if (persistedSearches.length > 0) {
      for (const p of persistedSearches) {
        const idx = localSearches.findIndex((s) => s.id === p.id);
        if (idx >= 0) {
          // merge, giving precedence to the latest persisted version
          // eslint-disable-next-line security/detect-object-injection
          localSearches[idx] = {
            // eslint-disable-next-line security/detect-object-injection
            ...localSearches[idx],
            ...p,
          };
        } else {
          localSearches.push(p);
        }
      }
    }
    if (localSearches.length === 0) {
      const newSearch = new DefaultSessionSearch();
      newSearch.isSelected = true;
      localSearches.push(newSearch);
    }
    for (const s of localSearches) {
      s.isSelected = false;
    }

    let selected: SessionSearch | undefined;
    if (persistedCurrentId) {
      selected = localSearches.find((s) => s.id === persistedCurrentId);
    }
    if (!selected) {
      selected = localSearches.find((s) => s.isSelected) || localSearches[0];
    }
    if (selected) {
      selected.isSelected = true;
      setCurrentSessionSearch(selected);
      selectionApplied.current = true;
    }

    setSessionSearches(localSearches);

    if (savedPresistance) {
      sessionStorage.removeItem('presistence-search');
    }
  }, [persistentSearches]);

  useEffect(() => {
    // Remove Searched if User updates project from Dropdown. Reaches DocExplorer -> Again comes back to previous project . Now navigates to Session Search
    return () => {
      const savedPresistance = sessionStorage.getItem('presistence-search');

      let persistedProjectId: string | number | null = null;
      let persistedUserRoleId: string | number | null = null;
      let persistedSecurityGroupId: string | number | null = null;

      if (savedPresistance) {
        try {
          const parsed = JSON.parse(savedPresistance);
          persistedProjectId = parsed.projectId ?? null;
          persistedUserRoleId = parsed.userRoleId ?? null;
          persistedSecurityGroupId = parsed.userSecurityGroupId ?? null;
        } catch {
          console.group();
        }
      }
      const hasContextChanged =
        (persistedProjectId !== null && persistedProjectId !== projectId) ||
        (persistedUserRoleId !== null && persistedUserRoleId !== userRoleId) ||
        (persistedSecurityGroupId !== null &&
          persistedSecurityGroupId !== userSecurityGroup);

      if (hasContextChanged) {
        dispatch(ClearShoppingCart());
        dispatch(PersistSearches([]));
        sessionStorage.removeItem('presistence-search');
      }
    };
  }, [location.pathname, projectId]);

  useEffect(() => {
    const searchId = currentSessionSearch.advancedSearchId ?? 0;
    const searchName =
      currentSessionSearch.name && currentSessionSearch.name.trim()
        ? currentSessionSearch.name
        : 'SearchName';

    getSearchUniqueDocIds({
      searchId,
      redirectToSearch: 'Advanced',
      searchName: searchName,
    });
  }, []);

  // STEP 3: Update UI properties
  useEffect(() => {
    if (!hasInitializedFromRedux.current) {
      return;
    }
    const restoredSearches = [...sessionSearches];
    const pinCount = restoredSearches.filter((i) => i.isPinned === true).length;
    const lessThanNinePinnedSearches = pinCount < 9;
    setCanPinItem(lessThanNinePinnedSearches);
    let searchesCount = restoredSearches.length;
    while (searchesCount > 10) {
      const unpinnedIndex = restoredSearches.findLastIndex(
        (i) => i.isPinned !== true
      );
      restoredSearches.splice(unpinnedIndex, 1);
      searchesCount = restoredSearches.length;
    }
    let ordinal = restoredSearches.length;
    for (const search of restoredSearches) {
      search.ordinal = ordinal;
      search.color = SessionSearchColors[ordinal - 1];
      if (!search.isSaved) {
        search.name = t('sessionSearch.listItems.name', {index: ordinal});
      }
      ordinal--;
    }

    if (!hasHandledBackNavigation.current && !selectionApplied.current) {
      for (const sessionSearch of restoredSearches) {
        sessionSearch.isSelected = false;
      }

      const currentSearch = restoredSearches.find(
        (value) => value.id === currentSessionSearch.id
      );

      if (currentSearch) {
        currentSearch.isSelected = true;
      } else {
        const newCurrentSearch = restoredSearches[0];
        newCurrentSearch.isSelected = true;
        setCurrentSessionSearch(newCurrentSearch);
      }
    }

    setDisplaySearches(restoredSearches);
    if (!canDiscardItem) {
      const savedAndExecutedSearches = restoredSearches.filter(
        (i) => i.isSaved && isExecuted(i)
      );
      const createSignature = (searches: SessionSearch[]) => {
        return searches
          .map(
            (s) =>
              `${s.id}:${s.isSaved}:${s.lastRuntime?.getTime?.() ?? 'none'}`
          )
          .sort((a, b) => a.localeCompare(b))
          .join('\n');
      };
      const currentSig = createSignature(persistentSearches || []);
      const newSig = createSignature(savedAndExecutedSearches);
      if (currentSig !== newSig) {
        const deepCopy = structuredClone(savedAndExecutedSearches);
        dispatch(PersistSearches(deepCopy));
      }
    }
  }, [sessionSearches, currentSessionSearch.id, canDiscardItem]);

  const searchRef = useRef(currentSessionSearch);

  useEffect(() => {
    searchRef.current = currentSessionSearch;
  }, [currentSessionSearch]);

  useEffect(() => {
    if (
      !hasInitializedFromRedux.current ||
      !sessionSearches.length ||
      currentSessionSearch.searchIsInProgress
    ) {
      return;
    }

    sessionStorage.setItem(
      'presistence-search',
      JSON.stringify({
        searches: sessionSearches,
        currentId: currentSessionSearch.id,
        projectId,
        userRoleId,
        userSecurityGroupId: userSecurityGroup,
      })
    );
  }, [
    currentSessionSearch,
    sessionSearches,
    projectId,
    userRoleId,
    userSecurityGroup,
  ]);

  const resetSearchState = () => {
    const updated = structuredClone(currentSessionSearch);
    updated.searchIsInProgress = false;
    updated.advancedSearchId = 0;
    updated.lastRuntime = undefined;

    setCurrentSessionSearch(updated);
    updateItemState(updated);
  };

  const handleSearchFailure = () => {
    resetSearchState();
    setToastType(ToastType.Error);
    setShowSearchToast(true);
    setSearchToastMessage(t('sessionSearch.unexpectedError'));
  };

  useEffect(() => {
    // Ignore all events if the current search is not running
    if (
      !searchRef.current.searchIsInProgress ||
      !searchRef.current.searchNotificationId
    )
      return;

    const reloadSearchResult = async (event: SignalREvent) => {
      if (!event) return;

      const outer = event.payload;
      const inner = outer?.payload ?? outer;
      const eventType = outer?.type ?? event.type;

      const isSuccess = eventType === SEARCHSUCCESS_SIGNALR_TYPE;
      const isFailed = eventType === SEARCHFAILED_SIGNALR_TYPE;
      if (!isSuccess && !isFailed) return;

      const searchId = inner?.searchID;
      if (!searchId) return;

      // Only process events for the active search
      if (searchRef.current.advancedSearchId !== searchId) return;

      // FAILED event
      if (isFailed) {
        handleSearchFailure();
        return;
      }
      if (
        !searchRef.current.searchIsInProgress ||
        !searchRef.current.searchNotificationId
      )
        return;
      // SUCCESS event → load result
      const request = toSearchResultRequest(searchRef.current);
      const data = await loadsearchResult(request);

      const anyError =
        data.pureHitStatus === 'ERROR' ||
        data.emailThreadStatus === 'ERROR' ||
        data.familyMemberStatus === 'ERROR' ||
        data.nearDuplicateStatus === 'ERROR';

      if (anyError) {
        handleSearchFailure();
        return;
      }

      const allComplete =
        data.pureHitStatus === 'COMPLETE' &&
        data.emailThreadStatus === 'COMPLETE' &&
        data.familyMemberStatus === 'COMPLETE' &&
        data.nearDuplicateStatus === 'COMPLETE';

      if (!allComplete) {
        // Update partial results
        const updated = structuredClone(searchRef.current);
        updated.searchResult = toSearchResult(data);
        setCurrentSessionSearch(updated);
        updateItemState(updated);
        searchRef.current = updated;

        return;
      }

      // Update current search
      const updated = structuredClone(searchRef.current);
      updated.searchIsInProgress = false;
      updated.searchResult = toSearchResult(data);

      setCurrentSessionSearch(updated);
      updateItemState(updated);
      searchRef.current = updated;
    };

    // Find the event that belongs to the current search
    const eventForCurrentSearch = signalREvents.find((evt) => {
      const type = evt?.payload?.type;
      const isRelevantType =
        type === SEARCHSUCCESS_SIGNALR_TYPE ||
        type === SEARCHFAILED_SIGNALR_TYPE;

      if (!isRelevantType) return false;

      const eventSearchId =
        evt.payload?.payload?.searchID ?? evt.payload?.searchID;

      return eventSearchId === searchRef.current.advancedSearchId;
    });

    if (eventForCurrentSearch) {
      reloadSearchResult(eventForCurrentSearch);
    }
  }, [
    signalREvents,
    currentSessionSearch.advancedSearchId,
    currentSessionSearch.searchNotificationId,
  ]);

  useEffect(() => {
    const persistentSearchesCount = displaySearches.filter(
      (i) => i.isSaved && isExecuted(i)
    ).length;
    const hasLessThanMaxSearches =
      displaySearches.length < 11 && hasAnyCriteriaAdded(currentSessionSearch);
    if (persistentSearchesCount === 10) {
      setCanAddItem(true);
    } else if (hasLessThanMaxSearches) {
      setCanAddItem(true);
    } else {
      setCanAddItem(false);
    }
  }, [currentSessionSearch]);

  useEffect(() => {
    if (canContinueWithSearch) {
      const doValidation = async () => {
        try {
          const metadataCriteria = currentSessionSearch.query?.find(
            (q) => q.type === QueryType.ContentAndMetadata
          );

          if (metadataCriteria) {
            const criteriaStr = getCriteriaValues(metadataCriteria);

            if (criteriaStr) {
              const result =
                await validateSearchCriteria.mutateAsync(criteriaStr);
              if (!result) {
                setShowSearchToast(true);
                setSearchToastMessage(
                  t('sessionSearch.searchValidationErrorToast')
                );
                setToastType(ToastType.Warning);
              } else {
                setReadyForSearch(true);
              }
            }
          } else {
            setReadyForSearch(true);
          }
        } catch (err: any) {
          const status = err?.response?.status || err?.status;
          if (status === 500) {
            setSearchToastMessage(
              t('sessionSearch.searchErrors.searchValidationError')
            );
            setToastType(ToastType.Error);
            setShowSearchToast(true);
            throw new Error(
              t('sessionSearch.errors.analyticInternalServerError')
            );
          }
          console.error('Caught error while search:', err.message);
        }
      };
      setcanContinueWithSearch(false);
      void doValidation();
    }
  }, [canContinueWithSearch]);

  //runsearch
  useEffect(() => {
    if (!readyForSearch) return;
    const doSearch = async () => {
      try {
        setReadyForSearch(false);

        const request = createSearchRequest(currentSessionSearch);
        await runsearch(request);
        //clear previous results if any
        const updated = structuredClone(searchRef.current);
        updated.searchIsInProgress = true;
        updated.searchNotificationId = 0;
        updated.searchResult = createEmptySearchResult();
        updated.searchResult.conceptualStatus = 'INPROGRESS';
        updated.searchResult.pureHitStatus = 'INPROGRESS';
        updated.searchResult.emailThreadStatus = 'INPROGRESS';
        updated.searchResult.familyMemberStatus = 'INPROGRESS';
        updated.searchResult.nearDuplicateStatus = 'INPROGRESS';

        setCurrentSessionSearch(updated);
        updateItemState(updated);
        // Start polling search status
        setShouldPollStatus(true);
      } catch (err: any) {
        console.error('Caught error while search:', err.message);
      }
    };
    setSearchIsInProgress(true);
    void doSearch();
  }, [readyForSearch]);

  useEffect(() => {
    if (!shouldPollStatus || !searchId) return;

    const intervalId = setInterval(async () => {
      try {
        const result: any = await refetchSearchStatus();
        if (result?.error) {
          const err = result.error as Error;
          console.log('Error fetching search status:', err);

          setSearchIsInProgress(false);
          handleSearchFailure();
        }
      } catch (err: any) {
        console.log('Error fetching search status:', err);
        setSearchIsInProgress(false);
        handleSearchFailure();
      }
    }, 2000);

    return () => {
      clearInterval(intervalId);
    };
  }, [shouldPollStatus, searchId]);

  useEffect(() => {
    if (!shouldPollStatus) return;
    if (
      searchStatusResponse?.searchStatus === 4 ||
      searchStatusResponse?.searchStatus === 1
    ) {
      setShouldPollStatus(false);
      setShouldPollResults(true);
    }
  }, [searchStatusResponse, shouldPollStatus]);

  useEffect(() => {
    if (shouldPollResults) {
      setPollPhase((prev) => prev ?? 'initial');
      setPollCount(0);
      setSearchIsInProgress(true);
    }
  }, [shouldPollResults]);

  const handleWaitForAllResults = () => {
    setShowSearchWaitModal(false);
    setSearchIsInProgress(true);

    setPollCount(0);

    setPollPhase((prev) => {
      if (prev === 'initial') return 'extended';
      if (prev === 'extended') return 'final';
      return 'final'; // stay in final phase
    });

    setShouldPollResults(true);
  };

  const handleNotifyPureHits = async () => {
    setShowSearchWaitModal(false);
    setSearchIsInProgress(false);
    setShouldPollResults(false);
    setPollPhase(null);
    const sessionSearch =
      currentSearchResultRequest?.sessionSearchList?.[0] ??
      ({} as SessionSearchItem);

    const notificationId = await sendNotification({
      searchId: currentSessionSearch.advancedSearchId ?? 0,
      notificationType: 'ADD',
      searchNotifyType: 'PH',
      sessionSearch,
    });
    const updated = structuredClone(currentSessionSearch);
    updated.searchNotificationId = notificationId;

    setCurrentSessionSearch(updated);
    updateItemState(updated);
    searchRef.current = updated;
    setSearchToastMessage(
      t('sessionSearch.searchBackgroundToast', {taskId: notificationId})
    );
    setToastType(ToastType.Success);
    setShowSearchToast(true);
  };

  const handleNotifyAll = async () => {
    setShowSearchWaitModal(false);
    setSearchIsInProgress(false);
    setShouldPollResults(false);
    setPollPhase(null);
    const sessionSearch =
      currentSearchResultRequest?.sessionSearchList?.[0] ??
      ({} as SessionSearchItem);

    const notificationId = await sendNotification({
      searchId: currentSessionSearch.advancedSearchId ?? 0,
      notificationType: 'ADD',
      searchNotifyType: 'ALL',
      sessionSearch,
    });

    const updated = structuredClone(currentSessionSearch);
    updated.searchNotificationId = notificationId;
    setCurrentSessionSearch(updated);
    updateItemState(updated);
    searchRef.current = updated;
    setSearchToastMessage(
      t('sessionSearch.searchBackgroundToast', {taskId: notificationId})
    );
    setToastType(ToastType.Success);
    setShowSearchToast(true);
  };

  //loadsearchResult
  useEffect(() => {
    if (!shouldPollResults || !currentSearchResultRequest) return;

    let intervalId: any = null;

    const pollResults = async () => {
      try {
        const data = await loadsearchResult(currentSearchResultRequest);

        if (
          data.pureHitStatus === 'ERROR' ||
          data.emailThreadStatus === 'ERROR' ||
          data.familyMemberStatus === 'ERROR' ||
          data.nearDuplicateStatus === 'ERROR'
        ) {
          setToastType(ToastType.Error);
          setShowSearchToast(true);
          setSearchToastMessage(t('sessionSearch.unexpectedError'));
          clearInterval(intervalId);
          setShouldPollResults(false);
          setSearchIsInProgress(false);
          currentSessionSearch.advancedSearchId = 0;
          currentSessionSearch.lastRuntime = undefined;

          return;
        }

        const done =
          data.pureHitStatus === 'COMPLETE' &&
          data.emailThreadStatus === 'COMPLETE' &&
          data.familyMemberStatus === 'COMPLETE' &&
          data.nearDuplicateStatus === 'COMPLETE';
        if (done) {
          clearInterval(intervalId);
          setShouldPollResults(false);
          setSearchIsInProgress(false);
          currentSessionSearch.searchIsInProgress = false;

          currentSessionSearch.advancedSearchId =
            currentSearchResultRequest.searchId;
          currentSessionSearch.lastRuntime = new Date();
          currentSessionSearch.searchResult = toSearchResult(data);
          updateItemState(currentSessionSearch);
          setCurrentSessionSearch({...currentSessionSearch});
          return;
        }

        // Not done → increment poll counter
        setPollCount((prev) => prev + 1);
        // Phase timeouts
        if (pollPhase === 'initial' && pollCount >= 8) {
          currentSessionSearch.advancedSearchId =
            currentSearchResultRequest.searchId;
          currentSessionSearch.lastRuntime = new Date();
          currentSessionSearch.searchIsInProgress = true;
          currentSessionSearch.searchResult = toSearchResult(data);
          updateItemState(currentSessionSearch);
          setCurrentSessionSearch({...currentSessionSearch});
          setShouldPollResults(false);
          clearInterval(intervalId);
          setSearchIsInProgress(false);
          setShowSearchWaitModal(true);
          return;
        }

        if (pollPhase === 'extended' && pollCount >= 11) {
          setShouldPollResults(false);
          clearInterval(intervalId);
          setSearchIsInProgress(false);
          setShowSearchWaitModal(true);
          return;
        }

        if (pollPhase === 'final' && pollCount >= 15) {
          setShouldPollResults(false);
          clearInterval(intervalId);
          setSearchIsInProgress(false);
          handleNotifyAll();
          return;
        }
      } catch {
        clearInterval(intervalId);
        handleSearchFailure();
        setShouldPollResults(false);
        setSearchIsInProgress(false);
      }
    };

    intervalId = setInterval(pollResults, 2000);
    return () => clearInterval(intervalId);
  }, [shouldPollResults, currentSearchResultRequest, pollPhase, pollCount]);

  const extractErrorCode = (msgs: string[]): string => {
    if (!msgs || msgs.length === 0) return '';
    const firstMsg = msgs[0];
    const parts = firstMsg.split(':');
    return parts[0].trim();
  };

  const extractErrorMessage = (msgs: string[]): string => {
    if (!msgs || msgs.length === 0) return '';
    const formatted = msgs.map((msg, index) => {
      const code = msg.split(':')[0].trim();
      const translated = getT(code);
      return `${index + 1}. ${translated}`;
    });
    return formatted.join(' </br></br> ');
  };

  const getT = (id: string) => {
    return t(`sessionSearch.searchErrors.${id}`);
  };

  const getErrorCode = (input: string): string => {
    const numericPart = input.replace(/^\D+/, '');
    return numericPart;
  };

  const removeAfterDot = (input: string): string => {
    const index = input.indexOf('.');
    if (index === -1) {
      return input;
    }
    return input.substring(0, index).trim();
  };

  const containsHtml = (input: string): boolean => {
    return /<\/?[a-z][^>\r\n]{0,1000}>/i.test(input);
  };

  // validatesearch
  const validateSearch = useCallback(
    async (session: SessionSearch): Promise<void> => {
      const isEmptyCriteria = (val?: string | null) =>
        !val || val.trim().length === 0;

      // --- Step 1: Analytics ---
      try {
        const analyticsCriteria = session.query?.find(
          (q) => q.type === QueryType.Analytics
        );

        if (analyticsCriteria) {
          const criteriaStr = getCriteriaValues(analyticsCriteria);
          if (isEmptyCriteria(criteriaStr)) {
            setReadyForSearch(false);
            setToastType(ToastType.Warning);
            setSearchToastMessage(
              t('sessionSearch.searchValidationErrorToast')
            );
            setShowSearchToast(true);
            throw new SearchValidationAbortError(
              t('sessionSearch.errors.abortAnalyticValidation')
            );
          }
          if (criteriaStr) {
            const result = await validateAnalytic.mutateAsync(criteriaStr);
            if (!result.searchStatus) {
              setShowAnalyticWorProductValidation(true);
              const rawError = getT(getErrorCode(result.error));
              const hasHtml = containsHtml(rawError);
              setErrorStringContainsHtml(hasHtml);
              const finalError = result.error?.startsWith('E')
                ? removeAfterDot(rawError)
                : rawError;
              setCriticalValidationError(finalError);
              throw new SearchValidationAbortError(
                t('sessionSearch.errors.abortAnalyticValidation')
              );
            }
          }
        }
      } catch (err: any) {
        if (err instanceof SearchValidationAbortError) {
          throw err;
        }
        const status = err?.response?.status || err?.status;
        if (status === 500) {
          setSearchToastMessage(
            t('sessionSearch.searchErrors.searchValidationError')
          );
          setToastType(ToastType.Error);
          setShowSearchToast(true);
          throw new Error(
            t('sessionSearch.errors.analyticInternalServerError')
          );
        } else {
          handleSearchFailure();
        }
        throw new Error(
          t('sessionSearch.errors.analyticValidationError', {
            message: err?.message || t('sessionSearch.errors.unknownError'),
          })
        );
      }

      // --- Step 2: WorkProduct ---
      try {
        const workProductCriteria = session.query?.find(
          (q) => q.type === QueryType.WorkProduct
        );

        if (workProductCriteria) {
          const criteriaStr = getCriteriaValues(workProductCriteria);
          if (isEmptyCriteria(criteriaStr)) {
            setReadyForSearch(false);
            setToastType(ToastType.Warning);
            setSearchToastMessage(
              t('sessionSearch.searchValidationErrorToast')
            );
            setShowSearchToast(true);
            throw new SearchValidationAbortError(
              t('sessionSearch.errors.abortWorkProductValidation')
            );
          }
          if (criteriaStr) {
            const result = await validateWorkProduct.mutateAsync(criteriaStr);
            if (!result.searchStatus) {
              setShowAnalyticWorProductValidation(true);

              const rawError = getT(getErrorCode(result.error));
              const hasHtml = containsHtml(rawError);

              setErrorStringContainsHtml(hasHtml);

              const finalError = result?.error?.startsWith('E')
                ? removeAfterDot(rawError)
                : rawError;

              setCriticalValidationError(finalError);

              throw new SearchValidationAbortError(
                t('sessionSearch.errors.abortWorkProductValidation')
              );
            }
          }
        }
      } catch (err: any) {
        if (err instanceof SearchValidationAbortError) {
          throw err;
        }
        const status = err?.response?.status || err?.status;
        if (status === 500) {
          setSearchToastMessage(
            t('sessionSearch.searchErrors.searchValidationError')
          );
          setToastType(ToastType.Error);
          setShowSearchToast(true);
          throw new Error(
            t('sessionSearch.errors.workProductInternalServerError')
          );
        } else {
          handleSearchFailure();
        }
        throw new Error(
          t('sessionSearch.errors.workProductValidationError', {
            message: err?.message || t('sessionSearch.errors.unknownError'),
          })
        );
      }

      // --- Step 3: Content & Metadata ---
      try {
        const contentMetaCriteria = session.query?.find(
          (q) => q.type === QueryType.ContentAndMetadata
        );

        if (contentMetaCriteria) {
          const criteriaStr = buildCriteriaString(contentMetaCriteria);
          if (isEmptyCriteria(criteriaStr)) {
            setReadyForSearch(false);
            setToastType(ToastType.Warning);
            setSearchToastMessage(
              t('sessionSearch.searchValidationErrorToast')
            );
            setShowSearchToast(true);
            throw new SearchValidationAbortError(
              t('sessionSearch.errors.abortContentMetadataValidation')
            );
          }
          if (criteriaStr) {
            const result = await validateContentMeta.mutateAsync(criteriaStr);
            if (!result.status) {
              let rawError = '';
              let hasHtml = true;
              if (result.msgs && result.msgs.length > 1) {
                rawError = extractErrorMessage(result.msgs);
              } else {
                rawError = getT(extractErrorCode(result.msgs));
                hasHtml = containsHtml(rawError);
              }
              setErrorStringContainsHtml(hasHtml);
              if (result.canContinue) {
                setContentMDValidationError(rawError);
                setShowContentMDValidation(true);
              } else {
                setShowAnalyticWorProductValidation(true);
                setCriticalValidationError(rawError);
              }
              throw new SearchValidationAbortError(
                t('sessionSearch.errors.abortContentMetadataValidation')
              );
            }
          }
        }
      } catch (err: any) {
        if (err instanceof SearchValidationAbortError) {
          throw err;
        }
        const status = err?.response?.status || err?.status;
        if (status === 500) {
          setSearchToastMessage(
            t('sessionSearch.searchErrors.searchValidationError')
          );
          setToastType(ToastType.Error);
          setShowSearchToast(true);
          throw new Error(
            t('sessionSearch.errors.contentMetadataInternalServerError')
          );
        } else {
          resetSearchState();
        }
        throw new Error(
          t('sessionSearch.errors.contentMetadataValidationError', {
            message: err?.message || t('sessionSearch.errors.unknownError'),
          })
        );
      }

      // --- Step 4: conceptual ---

      const conceptualCriteria = session.query?.find(
        (q) => q.type === QueryType.Conceptual
      );

      if (conceptualCriteria) {
        const criteriaStr = buildCriteriaString(conceptualCriteria);
        if (isEmptyCriteria(criteriaStr)) {
          setReadyForSearch(false);
          setToastType(ToastType.Warning);
          setSearchToastMessage(t('sessionSearch.searchValidationErrorToast'));
          setShowSearchToast(true);
          throw new Error(t('sessionSearch.errors.conceptualValidationFailed'));
        }
      }
    },
    [
      validateAnalytic,
      validateWorkProduct,
      validateContentMeta,
      validateSearchCriteria,
    ]
  );
  const updateItemState = (item: SessionSearch) => {
    setSessionSearches((prev) =>
      prev.some((value) => value.id === item.id)
        ? prev.map((value) => (value.id === item.id ? item : value))
        : [item, ...prev.map((value) => ({...value, isSelected: false}))]
    );
  };

  const handleSearchModifiersChanged = (item: SessionSearch) => {
    const clonedItem = structuredClone(item);
    updateItemState(clonedItem);
    setCurrentSessionSearch(clonedItem);
  };

  const handleItemSelected = (_index: number, item: SessionSearch) => {
    if (currentSessionSearch.id === item.id) {
      return;
    }
    if (canDiscardItem && currentSessionSearchBackup) {
      setCanDiscardItem(false);
      updateItemState(currentSessionSearchBackup);
      setCurrentSessionSearchBackup(null);
    }
    for (const sessionSearch of sessionSearches) {
      sessionSearch.isSelected = false;
    }
    item.isSelected = true;
    updateItemState(item);
    const clonedItem = structuredClone(item);
    setCurrentSessionSearch(clonedItem);
  };

  const handleItemPinned = (_index: number, item: SessionSearch) => {
    updateItemState(item);
  };

  const handleItemUnpinned = (_index: number, item: SessionSearch) => {
    updateItemState(item);
  };

  const handleItemDeleted = (_index: number, item: SessionSearch) => {
    let filteredSearches = sessionSearches.filter(
      (value) => value.id !== item.id
    );
    if (filteredSearches.length == 0) {
      const newSearch = new DefaultSessionSearch();
      newSearch.isSelected = true;
      filteredSearches = [newSearch];
    }
    setSessionSearches(filteredSearches);
  };

  const handleItemAdded = () => {
    if (!canAddItem) {
      console.error(t('sessionSearch.errors.maxSearchLimitReached'));
      return;
    }

    const newSearch = new DefaultSessionSearch();
    newSearch.isSelected = true;
    for (const sessionSearch of sessionSearches) {
      sessionSearch.isSelected = false;
    }
    const updatedSearches = [newSearch, ...sessionSearches];
    setSessionSearches(updatedSearches);
    setCurrentSessionSearch(newSearch);
  };

  const handleItemDiscarded = () => {
    setCanDiscardItem(false);
    if (currentSessionSearchBackup) {
      if (currentSessionSearch.id === currentSessionSearchBackup.id) {
        updateItemState(currentSessionSearchBackup);
      }
      setCurrentSessionSearch({...currentSessionSearchBackup});
      setCurrentSessionSearchBackup(null);
    }
  };

  const handleCopy = (_item: SessionSearch) => {
    if (sessionSearches.length >= 10) {
      return;
    }
    const copiedQuery = _item.query ? structuredClone(_item.query) : [];

    const queryWithNewIds = copiedQuery.map((q: Query) => ({
      ...q,
      value: q.value.map((c: Criteria) => ({
        ...c,
        id: uuidv4(),
      })),
    }));
    const copiedSearch: SessionSearch = {
      id: uuidv4(),
      name: '',
      familyMemberDocuments: _item.familyMemberDocuments,
      threadedEmailDocuments: _item.threadedEmailDocuments,
      nearDuplicateDocuments: _item.nearDuplicateDocuments,
      regexOnWorkProductAttributes: _item.regexOnWorkProductAttributes,
      isSelected: true,
      isPinned: false,
      isSaved: false,
      lastRuntime: undefined,
      advancedSearchId: undefined,
      searchResult: undefined,
      ordinal: undefined,
      color: undefined,
      query: queryWithNewIds,
    };
    const updatedSearches = sessionSearches.map((search) => ({
      ...search,
      isSelected: false,
    }));
    const newSearches = [copiedSearch, ...updatedSearches];
    setSessionSearches(newSearches);
    setCurrentSessionSearch(copiedSearch);
  };

  const handleSave = () => {
    setIsSaveDrawerOpen(true);
  };

  const handleModify = (item: SessionSearch) => {
    setCanDiscardItem(true);

    const queryWithNewIds = (item.query ?? []).map((q: Query) => ({
      ...q,
      value: q.value.map((c: Criteria) => ({
        ...c,
        id: uuidv4(),
      })),
    }));

    const updated: SessionSearch = {
      ...item,
      id: uuidv4(),
      isSelected: true,
      isPinned: false,
      isSaved: false,
      lastRuntime: undefined,
      advancedSearchId: undefined,
      searchResult: undefined,
      ordinal: undefined,
      color: undefined,
      query: queryWithNewIds,
    };

    const clonedUpdated = structuredClone(updated);
    setCurrentSessionSearch(clonedUpdated);

    const backup = structuredClone(item);
    setCurrentSessionSearchBackup(backup);
  };

  const handleSearch = async (item: SessionSearch) => {
    setSessionSearchItem(item);
    setCanDiscardItem(false);
    handleProceed(item);
  };

  const handleProceed = useCallback(
    async (sessionSearchItemData?: SessionSearch) => {
      try {
        await validateSearch(
          sessionSearchItemData ?? (sessionSearchItem as SessionSearch)
        ); //change
        setcanContinueWithSearch(true);
      } catch (err: any) {
        console.error(err);
      } finally {
        setOpenNestedSearchModal(false);
        setNestedChildModal(false);
      }
    },
    []
  );

  const handleItemModified = (item: SessionSearch) => {
    const clonedItem = structuredClone(item);
    setSessionSearches((prev) =>
      prev.map((value) => {
        if (value.id === item.id) {
          return clonedItem;
        } else {
          return value;
        }
      })
    );
    setCurrentSessionSearch(clonedItem);
  };

  const handleAddToShoppingCart = (type: ResultType): boolean => {
    if (!canPinItem) {
      setShowPinToast(true);
      return false;
    }
    const sessionSearchCopy = structuredClone(currentSessionSearch);
    if (sessionSearchCopy.searchResult) {
      switch (type) {
        case ResultType.PureHit:
          if (sessionSearchCopy.searchResult?.pureHit) {
            sessionSearchCopy.searchResult.pureHit.addedToShoppingCart = true;
          }
          break;
        case ResultType.FamilyMember:
          if (sessionSearchCopy.searchResult?.familyMember) {
            sessionSearchCopy.searchResult.familyMember.addedToShoppingCart = true;
          }
          break;
        case ResultType.EmailThread:
          if (sessionSearchCopy.searchResult?.emailThread) {
            sessionSearchCopy.searchResult.emailThread.addedToShoppingCart = true;
          }
          break;
        case ResultType.NearDupe:
          if (sessionSearchCopy.searchResult?.nearDuplicate) {
            sessionSearchCopy.searchResult.nearDuplicate.addedToShoppingCart = true;
          }
          break;
        case ResultType.Conceptual:
          if (sessionSearchCopy.searchResult)
            sessionSearchCopy.searchResult.ConceptualResultInShoppingCart = true;
          break;
        default:
          console.warn('Unknown ResultType:', type);
      }
    }
    sessionSearchCopy.isPinned = true;
    setCurrentSessionSearch(sessionSearchCopy);
    setSessionSearches((prev) =>
      prev.map((value) =>
        value.id === sessionSearchCopy.id ? sessionSearchCopy : value
      )
    );
    return true;
  };

  const handleResultRemoved = (id: string, type: ResultType) => {
    const updateSearch = (search: SessionSearch): SessionSearch => {
      if (search.id !== id || !search.searchResult) return search;
      const searchCopy = structuredClone(search);
      switch (type) {
        case ResultType.PureHit:
          if (searchCopy.searchResult?.pureHit) {
            searchCopy.searchResult.pureHit.addedToShoppingCart = false;
          }
          break;
        case ResultType.EmailThread:
          if (searchCopy.searchResult?.emailThread) {
            searchCopy.searchResult.emailThread.addedToShoppingCart = false;
          }
          break;
        case ResultType.FamilyMember:
          if (searchCopy.searchResult?.familyMember) {
            searchCopy.searchResult.familyMember.addedToShoppingCart = false;
          }
          break;
        case ResultType.NearDupe:
          if (searchCopy.searchResult?.nearDuplicate) {
            searchCopy.searchResult.nearDuplicate.addedToShoppingCart = false;
          }
          break;
        case ResultType.Conceptual:
          if (searchCopy.searchResult) {
            searchCopy.searchResult.ConceptualResultInShoppingCart = false;
          }
          break;
        default:
          console.warn('Unknown ResultType:', type);
      }
      const sr = searchCopy.searchResult;
      if (
        sr &&
        sr.pureHit?.addedToShoppingCart === false &&
        sr.emailThread?.addedToShoppingCart === false &&
        sr.familyMember?.addedToShoppingCart === false &&
        sr.nearDuplicate?.addedToShoppingCart === false
      ) {
        searchCopy.isPinned = false;
      }
      return searchCopy;
    };
    setSessionSearches((prev) => prev.map((s) => updateSearch(s)));
    if (currentSessionSearch.id === id) {
      setCurrentSessionSearch((prev) => updateSearch(prev));
    }
  };

  const onChangeNavigateAction = () => {
    const payload = {
      searches: sessionSearches,
      currentId: currentSessionSearch.id,
      projectId,
      userRoleId,
      userSecurityGroupId: userSecurityGroup,
    };
    sessionStorage.setItem('presistence-search', JSON.stringify(payload));
  };

  const renderRightPanel = () => {
    const resultMode =
      currentSessionSearch.lastRuntime || currentSessionSearch.advancedSearchId;
    const selectedSearchIds = sessionSearches
      .filter((s) => s.isPinned && s.advancedSearchId)
      .map((s) => s.advancedSearchId as number);
    if (resultMode) {
      return (
        <ResultShoppingCart
          searchId={currentSessionSearch.advancedSearchId}
          searchIds={selectedSearchIds}
          userRoleId={userRoleId}
          currentSessionId={currentSessionSearch.advancedSearchId}
          onItemRemoved={handleResultRemoved}
          onChangeNavigate={onChangeNavigateAction}
        />
      );
    } else {
      return (
        <SearchModifiers
          userRoleId={userRoleId}
          item={currentSessionSearch}
          onChange={handleSearchModifiersChanged}
        />
      );
    }
  };

  const handleItemSaved = (item: SessionSearch) => {
    updateItemState(item);
    setIsSaveDrawerOpen(false);
    setShowSaveSuccessToast(true);
  };

  return (
    <>
      <Grid container className="session-search-page">
        {/* Left Panel */}
        <Grid container size={3} className="session-search-left-panel">
          <Stack direction="column" sx={{width: '100%'}}>
            <Box className="session-search-left-header-panel">
              <LeftHeader />
            </Box>
            <Box className="session-search-search-list-panel">
              <SearchList
                items={displaySearches}
                canPin={canPinItem}
                onItemSelected={handleItemSelected}
                onItemPinned={handleItemPinned}
                onItemUnpinned={handleItemUnpinned}
                onItemDeleted={handleItemDeleted}
              />
            </Box>
          </Stack>
        </Grid>
        {/* Center Panel */}
        <Grid container size={6} className="session-search-center-panel">
          <Stack direction="column" sx={{width: '100%'}}>
            <Box className="session-search-center-header-panel">
              <CenterHeader
                uniqueDocIdCount={uniqueDocIdCount}
                canAdd={canAddItem}
                canDiscard={canDiscardItem}
                onAdd={handleItemAdded}
                onDiscardChanges={handleItemDiscarded}
              />
            </Box>
            <Box className="session-search-panel">
              <SearchQuery
                key={currentSessionSearch.id}
                userRoleId={userRoleId}
                item={currentSessionSearch}
                canEdit={canDiscardItem}
                onCopy={handleCopy}
                onSave={handleSave}
                onSearch={handleSearch}
                onModify={handleModify}
                onAddAnalyticsQuery={handleItemModified}
                onAddConceptualQuery={handleItemModified}
                onAddContentAndMetadataQuery={handleItemModified}
                onAddWorkProductQuery={handleItemModified}
                onQueryReordered={handleItemModified}
                onQueryDeleted={handleItemModified}
                onQueryModified={handleItemModified}
                OnAddToShoppingCart={handleAddToShoppingCart}
              />
            </Box>
          </Stack>
        </Grid>
        {/* Right Panel */}
        <Grid size={3} className="session-search-right-panel">
          {renderRightPanel()}
        </Grid>
      </Grid>
      {showAnalyticWorProductValidation && (
        <ArrModal
          showModal={showAnalyticWorProductValidation}
          onClose={() => setShowAnalyticWorProductValidation(false)}
          title={t('sessionSearch.searchValidationErrorTitle')}
          description={criticalValidationError}
          showClose={false}
          descriptionHasHtml={errorStringContainsHtml}
          dataTestId="show-analyticswork-modal"
          buttons={[
            {
              id: '1',
              label: t('sessionSearch.ok'),
              onClick: () => {
                setShowAnalyticWorProductValidation(false);
              },
              variant: 'primary',
              dataTestId: 'sam-ok-button',
            },
          ]}
        />
      )}
      <ArrModal
        showModal={nestedChildModal}
        onClose={() => setNestedChildModal(false)}
        showClose
        title={t('sessionSearch.nestedSearchModal.title')}
        description={t('sessionSearch.nestedSearchModal.description')}
        titleStartIcon={
          <InfoIcon
            className="info-icon nested-modal-title-icon"
            aria-label={t('sessionSearch.nestedSearchModal.iconAriaLabel')}
          />
        }
        dataTestId="nested-child-modal"
        buttons={[
          {
            id: '1',
            label: t('sessionSearch.nestedSearchModal.cancelButton'),
            onClick: () => {
              setNestedChildModal(false);
            },
            variant: 'secondary',
            dataTestId: 'ncm-cancel-button',
          },
          {
            id: '2',
            label: t('sessionSearch.nestedSearchModal.proceedButton'),
            onClick: () => {
              handleProceed();
            },
            variant: 'primary',
            dataTestId: 'ncm-proceed-button',
          },
        ]}
      />
      <ArrModal
        showModal={openNestedSearchModal}
        onClose={() => {
          setOpenNestedSearchModal(false);
        }}
        className={`nested-search-execute-modal`}
        title={t('savedSearch.executeNestedSearch.title')}
        ariaLabel={t('savedSearch.executeNestedSearch.title')}
        ariaDescription={t('savedSearch.executeNestedSearch.description')}
        dataTestId="nested-search-execute-modal"
      >
        <Box>
          <ArrBanner
            type="contextual"
            variant="complete"
            bannerContent={t(
              'savedSearch.executeNestedSearch.nestedInfoContent'
            )}
            startIcon={<CompleteIcon />}
            dataTestId="session-search-banner"
          />
          <div
            className="text-description-container"
            data-testid={`view-session-search-nested-modal-content`}
          >
            <Typography
              className="typo-text body3_size modal-description"
              data-testid="nested-search-modal-text"
            >
              {t('savedSearch.executeNestedSearch.nestedModalContent')}
            </Typography>
          </div>
          <Box className="nested-child-grid" data-testid="nested-child-table">
            <ArrDataGrid
              rowHeight={DEFAULT_ROW_HEIGHT}
              rows={rows}
              columns={nestedColumns}
              primaryKey="ID"
              className="nested-table-grid"
              getRowId={(rows) => rows.id}
            />
          </Box>
          <Box className="nested-modal-action-buttons">
            <ArrButton
              onClick={() => {
                setOpenNestedSearchModal(false);
              }}
              variant="secondary"
              dataTestId="view-sesssion-cancel-btn"
              aria-label={t('common.cancel')}
              className="modal-action-button"
            >
              {t('common.cancel')}
            </ArrButton>
            <Box className="nested-modal-button">
              <ArrButton
                aria-label={t('common.export')}
                variant="secondary"
                onClick={() => {}}
                className="modal-action-button"
                dataTestId="session-export-btn"
              >
                <div
                  className="button-content"
                  data-testid={`view-session-search-export`}
                >
                  <ExportIcon />
                  {t('common.export')}
                </div>
              </ArrButton>

              <ArrButton
                onClick={() => handleProceed()}
                variant="primary"
                dataTestId="session-proceed-btn"
                type="submit"
                aria-label={t('common.proceed')}
                className="modal-action-button"
              >
                {t('common.proceed')}
              </ArrButton>
            </Box>
          </Box>
        </Box>
      </ArrModal>
      {showContentMDValidation && (
        <ArrModal
          showModal={showContentMDValidation}
          onClose={() => setShowContentMDValidation(false)}
          title={t('sessionSearch.searchValidationErrorTitle')}
          description={contentMDValidationError}
          showClose={false}
          descriptionHasHtml={errorStringContainsHtml}
          dataTestId="show-content-mdvalidation-modal"
          buttons={[
            {
              id: '1',
              label: t('sessionSearch.no'),
              onClick: () => {
                setShowContentMDValidation(false);
              },
              variant: 'primary',
              dataTestId: 'scm-no-button',
            },
            {
              id: '2',
              label: t('sessionSearch.yes'),
              onClick: () => {
                setcanContinueWithSearch(true);
                setShowContentMDValidation(false);
              },
              variant: 'primary',
              dataTestId: 'scm-yes-button',
            },
          ]}
        >
          <Box className="modal-desc-text" data-testid="arr-modal-description">
            <p
              data-testid={`view-session-search-content-mdvalidation-question`}
            >
              {t('sessionSearch.ContentMDValidationQuestion')}
            </p>
            <p
              data-testid={`view-session-search-content-mdvalidation-instruction`}
            >
              {t('sessionSearch.ContentMDValidationInstruction')}
            </p>
          </Box>
        </ArrModal>
      )}
      {showSearchToast && (
        <ArrToastNotification
          message={searchToastMessage}
          onClose={() => setShowSearchToast(false)}
          status={[toastType]}
        />
      )}
      {(searchIsInProgress || isNestedSearchLoading) && (
        <ArrLoadingSpinner
          show={searchIsInProgress || isNestedSearchLoading}
          overlay={true}
          isOpen={searchIsInProgress || isNestedSearchLoading}
        />
      )}
      {isSaveDrawerOpen && (
        <SaveSearchDrawer
          item={currentSessionSearch}
          isOpen={isSaveDrawerOpen}
          onClose={() => setIsSaveDrawerOpen(false)}
          onSave={handleItemSaved}
        />
      )}
      {showSaveSuccessToast && (
        <ArrToastNotification
          message={t('sessionSearch.saveDrawer.saveSuccess')}
          onClose={() => setShowSaveSuccessToast(false)}
          status={[ToastType.Success]}
        />
      )}
      {showPinToast && (
        <ArrToastNotification
          message={t('sessionSearch.listItems.pinWarning')}
          status={[ToastType.Warning]}
          onClose={() => setShowPinToast(false)}
        />
      )}
      {showSearchWaitModal && (
        <ArrSearchProgressDialog
          showModal={showSearchWaitModal}
          title={t('common.arrSearchProgressDialog.title')}
          description={t('common.arrSearchProgressDialog.description')}
          buttons={[
            {
              id: '1',
              label: t('common.arrSearchProgressDialog.waitForAllResults'),
              onClick: handleWaitForAllResults,
              variant: 'primary',
              dataTestId: 'arr-search-wait-button',
            },
            {
              id: '2',
              label: t(
                'common.arrSearchProgressDialog.notifyWhenPureHitAreDone'
              ),
              onClick: handleNotifyPureHits,
              variant: 'secondary',
              dataTestId: 'arr-search-notifywhenphdone-button',
            },
            {
              id: '3',
              label: t(
                'common.arrSearchProgressDialog.notifyMeWhenResultsAreDone'
              ),
              onClick: handleNotifyAll,
              variant: 'secondary',
              dataTestId: 'arr-search-notifyme-button',
            },
          ]}
          onClose={() => setShowSearchWaitModal(false)}
        ></ArrSearchProgressDialog>
      )}
    </>
  );
};

export default ViewSessionSearch;
